import { Component, ElementRef, Input, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { PreviewService } from '@modules/preview/services/preview.service';
import { Clip } from '@modules/preview/types/clip';
import { StreamStatus, StreamStatusPlaybackLoop } from '@modules/preview/types/stream-status';
import { Timecode } from '@modules/preview/types/timecode';
import { Record } from '@modules/records/types/record';
import { BehaviorSubject, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'app-playback-actions',
    templateUrl: './playback-actions.component.html',
    styleUrl: './playback-actions.component.less',
    standalone: false
})
export class PlaybackActionsComponent {

  // ViewChild
  @ViewChild('cueToButton', { static: false }) cueToButtonRef?: ElementRef;

  // Inputs
  @Input() clip?: Clip;
  @Input() record?: Record;
  @Input() status?: StreamStatus;
  @Input() serverId?: string;

  // Inputs
  public disabled = false;
  public trimPoints: {start?: string, end?: string} = {};

  // Private
  private alive = new Subject<void>();
  private timecode?: string;

  /**
   * Constructor
   */

  constructor(
    public previewService: PreviewService,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  setTimecode(timecode: string): void {
    this.timecode = timecode;
  }

  getJumpTimecode(type: 'next' | 'prev'): string | undefined {
    let result;

    // Get timecodes
    let timecodes = [];
    let overtimeTrimPoints: {start?: string, end?: string} = {};
    if (this.record && this.serverId) {
      this.trimPoints = Record.getTrimPoints(this.record, this.serverId, false);
      overtimeTrimPoints = Record.getTrimPoints(this.record, this.serverId, true);
    }
    if (this.trimPoints?.start) { timecodes.push(this.trimPoints.start); }
    if (this.trimPoints?.end) { timecodes.push(this.trimPoints.end); }
    if (overtimeTrimPoints?.start) { timecodes.push(overtimeTrimPoints.start); }
    if (overtimeTrimPoints?.end) { timecodes.push(overtimeTrimPoints.end); }

    // Convert to Timecode class
    const currentTC = new Timecode(this.timecode || 0, (this.clip?.timebase || 24), !!this.clip?.drop);
    if (this.timecode && timecodes.length) {
      timecodes = timecodes.map(tc => new Timecode(tc, (this.clip?.timebase || 24), !!this.clip?.drop) ).sort((a, b) => a.frameCount - b.frameCount);
    }

    // Next
    if (type === 'next') {
      timecodes = (timecodes as Timecode[]).filter(tc => tc.frameCount > currentTC.frameCount);
      if (timecodes.length) {
        result = timecodes[0].toString();
      }
    }

    if (type === 'prev') {
      timecodes = (timecodes as Timecode[]).filter(tc => tc.frameCount < currentTC.frameCount);
      if (timecodes.length) {
        result = timecodes[timecodes.length - 1].toString();
      }
    }

    return result;
  }

  /**
   * Actions
   */

  play(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.play(this.record?.id, [this.serverId]);
    }
  }

  reverse(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.reverse(this.record?.id, [this.serverId]);
    }
  }

  fastForward(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.fastForward(this.record?.id, [this.serverId], 4);
    }
  }

  rewind(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.rewind(this.record?.id, [this.serverId], 4);
    }
  }

  pause(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.pause(this.record?.id, [this.serverId]);
    }
  }

  goToStart(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToStart(this.record?.id, [this.serverId]);
    }
  }

  goToEnd(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToEnd(this.record?.id, [this.serverId]);
    }
  }

  jump(value: {tc?: string, frame?: number, offset?: number}): void {
    if (this.record?.id && this.serverId) {
      this.previewService.jump(this.record?.id, [this.serverId], value);
    }
  }

  goToNextClip(stream?: any): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToNextClip(this.record?.id, [this.serverId]);
    }
  }

  goToPrevClip(stream?: any): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToPrevClip(this.record?.id, [this.serverId]);
    }
  }

  loopPlayback(): void {
    if (this.record?.id && this.serverId) {
      const value: StreamStatusPlaybackLoop = this.status?.playback?.loopPlayback === 'off' ? 'continuous' : 'off';
      this.previewService.loopPlayback(this.record?.id, [this.serverId], value);
    }
  }

  goToNextAndPlay(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToNextAndPlay(this.record?.id, [this.serverId]);
    }
  }

  goToNextAndPause(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToNextAndPause(this.record?.id, [this.serverId]);
    }
  }

  goToPrevAndPlay(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToPrevAndPlay(this.record?.id, [this.serverId]);
    }
  }

  goToPrevAndPause(): void {
    if (this.record?.id && this.serverId) {
      this.previewService.goToPrevAndPause(this.record?.id, [this.serverId]);
    }
  }

  jumpInPoint(): void {
    const tc = this.getJumpTimecode('prev');
    if (tc) {
      this.jump({ tc });
    }
  }

  jumpOutPoint(): void {
    const tc = this.getJumpTimecode('next');
    if (tc) {
      this.jump({ tc });
    }
  }

}
